import React, { useEffect, useState } from 'react';
import { Routes } from '../Routing';
import LabSimService, { StateContext } from '../services/LabSimService';
import ProductListEntry from '../controls/ProductListEntry';
import LoadingPage from './LoadingPage';
import PageFooter from '../partials/PageFooter';
import { T } from '@testout/labsim-ui-atomics';
import styles from './ProductSelection.module.less';
import { useStateContext } from '../App';
import moment from 'moment';
const ProductSelection = ({ history }) => {
    const [displayLicensedOnly, setDisplayLicensedOnly] = useState(false);
    const [newReleasedOnly, setNewReleasedOnly] = useState(false);
    const [displayCampusProductOnly, setDisplayCampusProductOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { getMigratedToLockedProducts, getMigratedFromLockedProducts, lockedVersions } = useStateContext();
    const [selectedLocale, setSelectedLocale] = useState('en');
    const [locales, setLocales] = useState([]);
    useEffect(() => {
        LabSimService.getLocaleOptions().then((locales) => {
            setLocales(locales);
        });
    }, []);
    const selectProduct = (selectedProduct) => {
        history.push({
            pathname: Routes.SELECT,
            search: `?isbn=${selectedProduct.isbn}&productId=${selectedProduct.productId}`,
        });
    };
    const isProductNewRelease = (releaseDate) => {
        const sixMonthsAgo = moment.utc();
        sixMonthsAgo.subtract(6, 'months');
        return moment(releaseDate).isAfter(sixMonthsAgo);
    };
    const getAvailableProducts = (products, isLTIAdvantage) => {
        const migratedToLockedProducts = getMigratedToLockedProducts();
        const migratedFromLockedProducts = getMigratedFromLockedProducts();
        let availableProducts = (products.isbns || [])
            // filter to only licensed product if filter is on
            .filter((p) => {
            var _a;
            return ((displayLicensedOnly && ((_a = products.licensed) === null || _a === void 0 ? void 0 : _a.indexOf(p.isbn)) !== -1) ||
                !displayLicensedOnly);
        })
            .filter((p) => {
            if (migratedToLockedProducts.length === 0)
                return true;
            return !migratedToLockedProducts.some((i) => i.isbn === p.isbn);
        })
            .filter((p) => {
            if (migratedFromLockedProducts.length === 0)
                return true;
            return !migratedFromLockedProducts.some((i) => i.isbn === p.isbn);
        })
            //filter based on lockedVersions
            .filter((p) => {
            if (lockedVersions && lockedVersions.length > 0) {
                const matchingProduct = lockedVersions.find((l) => l.isbn === p.isbn);
                if (matchingProduct != null) {
                    return p.versions.some((v) => v.productVersionId === matchingProduct.productVersionId);
                }
            }
            return true;
        });
        if (!isLTIAdvantage)
            availableProducts = availableProducts.filter((p) => {
                return p.isCampusProduct;
            });
        if (isLTIAdvantage) {
            availableProducts = availableProducts
                .filter((p) => {
                if (!newReleasedOnly)
                    return true;
                return p.versions.some((version) => isProductNewRelease(version.releaseDate));
            })
                .filter((p) => {
                if (!displayCampusProductOnly)
                    return true;
                return p.isCampusProduct;
            })
                .filter((p) => {
                const normalizeString = (str) => str.toLowerCase();
                const search = normalizeString(searchQuery);
                const isbnMatch = normalizeString(p.isbn).includes(search);
                const titleMatch = normalizeString(p.title).includes(search);
                return isbnMatch || titleMatch;
            });
        }
        return availableProducts;
    };
    const getProductList = (products, availableProducts) => {
        const licenseTooltip = (React.createElement("div", { className: 'tooltip-text text-width-med' }, "A license for this product has been set up by your administrator. Contact your CompTIA representative for details."));
        return availableProducts
            .filter((product) => {
            if (!selectedLocale)
                return true;
            if (selectedLocale === 'en' && product.locale === 'en-us')
                return true;
            return product.locale === selectedLocale;
        })
            .map((product) => {
            var _a;
            const licensed = ((_a = products.licensed) === null || _a === void 0 ? void 0 : _a.indexOf(product.isbn)) !== -1 || false;
            return (React.createElement(ProductListEntry, { key: product.productId, product: product, licensed: licensed, licenseTooltip: licenseTooltip, onSelect: selectProduct }));
        });
    };
    const goBack = () => {
        history.push(Routes.HOME);
    };
    return (React.createElement(StateContext.Consumer, null, (stateContext) => {
        var _a;
        if (stateContext.productsLoaded) {
            if ((_a = stateContext.products) === null || _a === void 0 ? void 0 : _a.isbns) {
                const isLtiAdv = stateContext.isIntegrationLTIAdvantage();
                const availableProducts = getAvailableProducts(stateContext.products, isLtiAdv);
                const productList = getProductList(stateContext.products, availableProducts);
                return (React.createElement("div", { className: styles.container },
                    React.createElement("div", null,
                        React.createElement(T.Text.H1, { variation: T.Text.Variation.H1_Large, automationId: 'title', className: styles.header }, "Select Product"),
                        !isLtiAdv && (React.createElement("div", null,
                            React.createElement(T.Input.Checkbox, { checked: displayLicensedOnly, onChange: setDisplayLicensedOnly, name: 'licensedOnly', automationId: 'licensedOnly' }, "Only show institution licensed products"))),
                        isLtiAdv && (React.createElement("div", { className: styles.checkboxesGroup },
                            React.createElement("div", { className: 'd-flex justify-content-between' },
                                React.createElement("div", null,
                                    React.createElement(T.Input.Checkbox, { checked: displayLicensedOnly, onChange: setDisplayLicensedOnly, name: 'licensedOnly', automationId: 'licensedOnly' }, "Institution-licensed products"),
                                    React.createElement(T.Input.Checkbox, { checked: displayCampusProductOnly, onChange: setDisplayCampusProductOnly, name: 'campusProductOnly', automationId: 'campusProductOnly' }, "Products currently in use"),
                                    React.createElement(T.Input.Checkbox, { checked: newReleasedOnly, onChange: setNewReleasedOnly, name: 'newReleasedOnly', automationId: 'newReleasedOnly' }, "New releases")),
                                React.createElement(T.Input.Select, { className: 'width-10rem', automationId: 'assignment-group', onChange: (newLocale) => {
                                        setSelectedLocale(newLocale);
                                    }, value: selectedLocale }, locales === null || locales === void 0 ? void 0 : locales.map((locale) => (React.createElement(T.Input.Select.Option, { value: locale.languageCode, key: locale.localeId }, locale.name))))),
                            React.createElement(T.Input.Search, { automationId: 'searchProducts', onSearch: setSearchQuery, screenReaderDescription: ' search' }))),
                        React.createElement("div", { className: styles.productContainer },
                            React.createElement("div", { className: styles.productList },
                                productList.length > 0 && productList,
                                productList.length === 0 && (React.createElement("div", { className: styles.notFound },
                                    React.createElement(T.Text.P, { automationId: 'notFound' },
                                        !isLtiAdv && React.createElement(React.Fragment, null, "No licensed products found."),
                                        isLtiAdv && (React.createElement(React.Fragment, null, "No courses match the filter and search criteria."))),
                                    isLtiAdv && (React.createElement(T.Text.Span, { automationId: 'missingProd', variation: T.Text.Variation.Small }, "If a product is missing that you expected to see licensed, contact your CompTIA Representative."))))),
                            displayLicensedOnly && productList.length > 0 && (React.createElement(T.Text.P, { automationId: 'ifYouDont', variation: T.Text.Variation.Small }, "If you don\u2019t see the product you are looking for, contact your CompTIA Representative.")),
                            React.createElement(T.ActionToolbar, { className: styles.bottom },
                                React.createElement(T.ActionToolbar.Group, { right: true },
                                    React.createElement(T.Button, { automationId: 'backBtn', onClick: goBack, variation: T.ButtonVariation.Secondary }, "Back"))))),
                    React.createElement("div", null,
                        React.createElement(PageFooter, null))));
            }
            else {
                return (React.createElement("div", { className: styles.notFound },
                    React.createElement(T.Text.H1, { automationId: 'title' }, "Your campus does not have any licensed products"),
                    React.createElement(T.Text.P, { automationId: 'please' }, "Please contact your representative to inquire about product licenses."),
                    React.createElement(T.Button, { automationId: 'browseBackButton', onClick: history.goBack, variation: T.ButtonVariation.Secondary }, "Back")));
            }
        }
        else {
            return React.createElement(LoadingPage, { progressTitle: 'Loading...', status: 'Loading products' });
        }
    }));
};
export default ProductSelection;
//# sourceMappingURL=ProductSelection.js.map